import type { Column } from "@tanstack/react-table";
import { type ClassValue, clsx } from "clsx";
import type { CSSProperties } from "react";
import { twMerge } from "tailwind-merge";
import type { ColumnType } from "../components/table/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const EMPTY_SIGN = "-";
export function formatCellValue(
  value: unknown,
  type: ColumnType,
  placeholder = EMPTY_SIGN,
): { value: string; showTooltip: boolean } {
  let displayValue = "";
  let isEmpty = false;

  switch (type) {
    case "numeric":
    case "amount":
      isEmpty = value === undefined || value === null || value === "";
      break;
    case "text":
      isEmpty =
        value === undefined || value === null || String(value).trim() === "";
      break;
    default:
      isEmpty = value === null || value === undefined || value === "";
  }

  displayValue = isEmpty ? placeholder : String(value);

  return {
    value: displayValue,
    showTooltip: displayValue !== placeholder,
  };
}

export function getPinningStyles<T>(
  column: Column<T>,
  isHeader = false,
): CSSProperties {
  const isPinned = column.getIsPinned();

  return {
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? (isHeader ? 3 : 2) : 1,
    margin: 0,
    isolation: isPinned ? "isolate" : undefined,
  };
}

export function getPinningCellClassName<T>(
  column: Column<T>,
  isHeader = false,
) {
  const isPinned = column.getIsPinned();

  return cn({
    "bg-white": isPinned,
    "group-hover:bg-blue-50": isPinned && !isHeader,
  });
}

export function interval(n: number, start = 1, step = 1): number[] {
  if (n <= 0) return [];
  return Array.from({ length: n }, (_, i) => start + i * step);
}

/**
 * Generates a pagination range with ellipsis for large page counts.
 * Uses a sliding window approach to show pages around the current page.
 * Always returns 7 items (or all pages if totalPages <= 7) to prevent layout shifts.
 *
 * @param currentPage - The current active page (0-indexed)
 * @param totalPages - Total number of pages
 * @param delta - Number of pages to show on each side of current page (default: 1)
 * @returns Array of page indices and 'ellipsis' markers (always 7 items when totalPages > 7)
 *
 * @example
 * generatePaginationRange(0, 20, 1)  // [0, 1, 2, 3, 4, 'ellipsis', 19]
 * generatePaginationRange(9, 20, 1)  // [0, 'ellipsis', 8, 9, 10, 'ellipsis', 19]
 * generatePaginationRange(19, 20, 1) // [0, 'ellipsis', 15, 16, 17, 18, 19]
 */
export function generatePaginationRange(
  currentPage: number,
  totalPages: number,
  delta = 1,
): (number | "ellipsis")[] {
  // Show all pages if 7 or fewer
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  const range: (number | "ellipsis")[] = [];
  const TOTAL_ITEMS = 7; // Always maintain 7 items to prevent resizing

  // Always include first page
  range.push(0);

  // Calculate the ideal range around current page
  let start = currentPage - delta;
  let end = currentPage + delta;

  // Determine if we need ellipsis on each side
  const needsStartEllipsis = start > 1;
  const needsEndEllipsis = end < totalPages - 2;

  if (!needsStartEllipsis && needsEndEllipsis) {
    // Near start: [0, 1, 2, 3, 4, 'ellipsis', last] = 7 items
    // Total: 1 (first) + 4 (pages) + 1 (ellipsis) + 1 (last) = 7
    start = 1;
    end = TOTAL_ITEMS - 3; // 7 - 3 = 4, so pages 1-4
  } else if (needsStartEllipsis && !needsEndEllipsis) {
    // Near end: [0, 'ellipsis', n-5, n-4, n-3, n-2, last] = 7 items
    // Total: 1 (first) + 1 (ellipsis) + 4 (pages) + 1 (last) = 7
    start = totalPages - 1 - (TOTAL_ITEMS - 3); // totalPages - 1 - 4
    end = totalPages - 2;
  } else {
    // Middle with both ellipses: [0, 'ellipsis', cur-1, cur, cur+1, 'ellipsis', last] = 7 items
    start = Math.max(1, currentPage - delta);
    end = Math.min(totalPages - 2, currentPage + delta);
  }

  // Add ellipsis after first page if there's a gap
  if (start > 1) {
    range.push("ellipsis");
  }

  // Add pages around current
  for (let i = start; i <= end; i++) {
    range.push(i);
  }

  // Add ellipsis before last page if there's a gap
  if (end < totalPages - 2) {
    range.push("ellipsis");
  }

  // Always include last page
  range.push(totalPages - 1);

  return range;
}
